// src/views/Callbacks.jsx
// ═══════════════════════════════════════════════════════════════
// CALLS TO RETURN — the Command Center's top panel + New Call's quick list
// ═══════════════════════════════════════════════════════════════
// Reece, 10/10/26: the missed calls and voicemails not returned yet, and how
// long each has waited — and NOTHING else: no bell, badge, notification,
// reminder, follow-up date or nag. Rules = src/callbacks.js (pure, tested);
// the call log = src/ringcentral.js fetchCallLog; Done marks = contact_log
// rows (scenarioStore.logContact, kind "callback-done").
//
// WHEN THE CALL LOG IS READ — only:
//   • when a list opens (the Command Center panel, New Call's Find screen),
//   • on its Refresh button (both lists have one),
//   • ~45 s after a call ends (the telephony WebSocket's Disconnected event —
//     App → noteCallEnded) while a list is on screen — and when a list opens
//     before a read has caught up with the last hang-up (called back from
//     New Call's lead card, then "← Someone else"), one read at hang-up +
//     45 s, so the call he just made is in it,
// and never more than once a minute (readGate), waiting out RingCentral's
// Retry-After on 429. There is NO interval timer that reads: RingCentral says
// polling the call log to imitate real time is not a supported use, and its
// "Heavy" group allows 10 reads a minute. The only repeating timer here
// re-draws the wait times once a minute from what was already read.
//
// ONE STORE PER TAB (module state + useSyncExternalStore), shared by both
// lists, so the console opening over the Command Center doesn't read twice.
// Reset at sign-out (App's resetSessionState → resetCallbacksSession).
//
// Reece only: both lists render for access.dashboard / access.newCall — Max
// has neither (and never gets RingCentral).

import React, { useEffect, useMemo, useRef, useState } from "react";
import { C } from '../theme';
import { CallBtn } from '../ui';
import * as RC from '../ringcentral';
import { staffAccess } from '../access';
import { readCallLog, searchLeadsByContacts, outboundByPhones, fetchLeadsByIds, prospectFromLead, prospectFromLoan } from '../lib';
import { logContact, fetchCallbackDone } from '../scenarioStore';
import { lookupKeys } from '../contactLog';
import {
  buildCallbacks, callLogUsable, callerLabel, doneCutoff, doneMarksFrom, doneRow, formatNumber, formatWait, linkCallers,
  lookbackFrom, numberKey, ownNumbers, readGate, waitLevel,
  ALL_RETURNED, CALLBACK_AFTER_HANGUP_MS, CALLBACK_DONE_DAYS, CALLBACK_LOOKBACK_DAYS, DONE_REASONS, HIDDEN_LABEL, PERMISSION_LINE,
} from '../callbacks';

// This device's Done marks, used only when contact_log can't take them.
export const DONE_LOCAL_KEY = "loanflow_callback_done_v1";

// ═══════ the store ═══════
function fresh() {
  return {
    status: "idle",        // idle | loading | ok | error
    error: null,           // the last read's error code (ringcentral.fetchCallLog)
    records: null,         // the last good read; null = nothing to show from RingCentral
    readAt: 0,             // when that read came back
    lastTryAt: 0,          // when the last read STARTED (the 60-second floor counts from here)
    blockedUntil: 0,       // RingCentral's Retry-After
    doneRows: [],          // callback-done rows from contact_log (every device's)
    doneShared: null,      // true: read from the table · false: the table can't take a mark (no database / table)
    doneReadFailed: false, // the last read of the Done marks failed (the table is there) — the last rows are kept
    doneSession: {},       // marks made in this tab {number: ms} (the row goes at once)
    deviceOnly: false,     // a Done couldn't reach the table → kept on this device
    found: { leads: [], outbound: [] },   // the database lookups' finds (this session)
    version: 0,
  };
}
let S = fresh();
let gen = 0;
let inflight = null;
let hangupTimer = null;
let hangupAt = 0;           // the last hang-up this session (RingCentral's Disconnected event)
let wakeTimer = null;
let lastSettings = null, lastAuthed = false;
const asked = new Set(), busy = new Set();   // numbers looked up in the database this session
// Numbers whose database lookup FAILED (a timeout, offline…): not asked again
// until the next read starts or a list opens — never straight away, which
// used to re-ask in a tight loop (review 10/10).
const failed = new Set();
const listeners = new Set();
function patch(p) {
  S = Object.assign({}, S, p || {}, { version: S.version + 1 });
  listeners.forEach(function (fn) { try { fn(); } catch (e) { /* a list went away */ } });
}
function subscribe(fn) { listeners.add(fn); return function () { listeners.delete(fn); }; }
function snapshot() { return S; }
export function callbackState() { return S; }

function readLocalDone() {
  try {
    const o = JSON.parse(window.localStorage.getItem(DONE_LOCAL_KEY) || "{}");
    return o && typeof o === "object" && !Array.isArray(o) ? o : {};
  } catch (e) { return {}; }
}
function writeLocalDone(number, iso, now) {
  try {
    const o = readLocalDone();
    if (!(Date.parse(o[number]) > Date.parse(iso))) o[number] = iso;
    const cut = now - CALLBACK_DONE_DAYS * 864e5;
    Object.keys(o).forEach(function (k) { if (!(Date.parse(o[k]) >= cut)) delete o[k]; });
    window.localStorage.setItem(DONE_LOCAL_KEY, JSON.stringify(o));
  } catch (e) { /* storage blocked — this tab's mark still hides the row */ }
}
// One re-draw when the floor / Retry-After ends, so Refresh comes back on.
// A one-shot timer — it never reads.
function armWake(ms) {
  if (wakeTimer || !listeners.size) return;
  wakeTimer = setTimeout(function () { wakeTimer = null; patch({}); }, Math.max(0, ms) + 50);
}

// Read the call log (+ every device's Done marks) — gated by readGate.
// Resolves true when a read came back with the list.
export function refreshCallbacks(settings, rcAuthed) {
  if (!callLogUsable(settings, rcAuthed)) { lastSettings = null; return Promise.resolve(false); }
  lastSettings = settings; lastAuthed = rcAuthed;
  if (inflight) return inflight;
  const now = Date.now();
  const g = readGate(S, now);
  if (!g.ok) { armWake(g.waitMs); return Promise.resolve(false); }
  const mine = gen;
  failed.clear();   // a new read: a lookup that failed may be tried once more
  patch({ status: "loading", lastTryAt: now });
  const p = Promise.all([
    Promise.resolve().then(function () { return RC.fetchCallLog(settings, { dateFrom: lookbackFrom(now, CALLBACK_LOOKBACK_DAYS) }); })
      .catch(function () { return { ok: false, error: "network" }; }),
    Promise.resolve().then(function () { return fetchCallbackDone(lookbackFrom(now, CALLBACK_DONE_DAYS)); })
      .catch(function () { return { ok: false, rows: [] }; }),
  ]).then(function (r) {
    if (mine !== gen) return false;
    const log = r[0] || {}, done = r[1] || {};
    // Done marks: read → the table's rows. No database / no table
    // (`unavailable`) → a mark would land on this device, so the list says
    // so. A read that merely FAILED keeps the last rows and says only that —
    // never "saved on this device only": Done still writes to the table
    // (review CB-7, 10/10).
    const next = {
      doneRows: done.ok ? (done.rows || []) : S.doneRows,
      doneShared: done.ok ? true : done.unavailable ? false : S.doneShared,
      doneReadFailed: !done.ok && !done.unavailable,
    };
    if (log.ok) Object.assign(next, { status: "ok", error: null, records: log.records || [], readAt: Date.now() });
    else {
      Object.assign(next, { status: "error", error: log.error || "network" });
      if (log.error === "rate_limited") next.blockedUntil = Date.now() + Math.max(0, Number(log.retryAfterMs) || 60000);
      // Without the permission / a login the old list can't be checked any more.
      if (log.error === "no_permission" || log.error === "not_authenticated") next.records = null;
    }
    patch(next);
    return !!log.ok;
  }).finally(function () { if (inflight === p) inflight = null; });
  inflight = p;
  return p;
}

// App: a call ended (RingCentral's Disconnected event). ~45 s later — the log
// needs 15–30 s to show it — the list is read again, if one is on screen.
// Nobody looking → only the time is kept: the next list that opens before a
// read has caught up with it (New Call: tap a row, call back, "← Someone
// else") reads once more at hang-up + 45 s (armHangupRead from the list's
// open). One-shot timers only; a read the floor blocks waits for the floor,
// once; a read already started after hang-up + 45 s makes it unneeded.
export function noteCallEnded() {
  hangupAt = Date.now();
  armHangupRead();
}
function hangupReadDue() { return hangupAt > 0 && S.lastTryAt < hangupAt + CALLBACK_AFTER_HANGUP_MS; }
function armHangupRead() {
  if (!listeners.size || !lastSettings || !hangupReadDue()) return;
  clearTimeout(hangupTimer);
  const mine = gen;
  const later = function () {
    hangupTimer = null;
    if (mine !== gen || !listeners.size || !lastSettings || !hangupReadDue()) return;
    if (inflight) { inflight.then(later, later); return; }   // a read that started too early: wait for it, then check again
    const g = readGate(S, Date.now());
    if (!g.ok) { hangupTimer = setTimeout(later, g.waitMs + 50); return; }
    refreshCallbacks(lastSettings, lastAuthed);
  };
  hangupTimer = setTimeout(later, Math.max(0, hangupAt + CALLBACK_AFTER_HANGUP_MS - Date.now()));
}

// Done: the row goes at once; one contact_log row is written (per account —
// every device sees it). If the table can't take it, it is kept on this
// device ("Done marks saved on this device only"). Practice mode: nothing.
// The mark covers the misses the row SHOWED — through its newest one
// (callbacks.doneCutoff), not "now": a call that came in after the last read
// and hasn't been seen yet stays on the list (review 10/10).
export function markCallbackDone(row, reason, ctx) {
  const c = ctx || {};
  if (!row || !row.number) return Promise.resolve({ ok: false });
  if (c.practice) return Promise.resolve({ ok: false, practice: true });
  const now = Date.now();
  const cut = doneCutoff(row, S.readAt, now);
  const iso = new Date(cut).toISOString();
  const add = {}; add[row.number] = Math.max(cut, Number(S.doneSession[row.number]) || 0);
  patch({ doneSession: Object.assign({}, S.doneSession, add) });
  const link = c.link || null;
  const loan = link && link.loan, lead = link && link.lead;
  const rec = doneRow({
    number: row.number, reason: reason, at: iso,
    loanId: loan ? loan.id : null, leadId: lead ? lead.id : (loan && loan.sourceLeadId) || null,
    name: (loan && loan.b) || (lead && lead.name) || row.callerName || "",
  });
  const mine = gen;
  return Promise.resolve().then(function () { return logContact(rec); }).catch(function () { return false; })
    .then(function (ok) {
      if (mine !== gen) return { ok: !!ok };
      if (!ok) { writeLocalDone(row.number, iso, now); patch({ deviceOnly: true }); }
      return { ok: !!ok };
    });
}

// Numbers nobody in memory has → the database, once a session each (lib.js's
// batched lookups through contactLog.lookupKeys): a lead's phone (only while
// never-mailed leads aren't loaded) and the skip-traced numbers
// (outbound_contacts.phone_digits — matched against, never copied).
export function lookupCallers(numbers, opts) {
  const o = opts || {};
  const todo = (numbers || []).filter(function (n) { return n && n.length === 10 && !asked.has(n) && !busy.has(n) && !failed.has(n); });
  if (!todo.length) return Promise.resolve(false);
  todo.forEach(function (n) { busy.add(n); });
  const keys = [];
  todo.forEach(function (n) { if (o.allLeadsLoaded === false) keys.push("p" + n); keys.push("o" + n); });
  const mine = gen;
  return Promise.resolve().then(function () {
    return lookupKeys(keys, { searchLeadsByContacts: searchLeadsByContacts, outboundByPhones: outboundByPhones, fetchLeadsByIds: fetchLeadsByIds }, { haveLead: o.haveLead });
  }).then(function (r) {
    if (mine !== gen) return false;
    todo.forEach(function (n) { busy.delete(n); });
    const settled = new Set((r && r.settled) || []);
    // A number whose request failed waits for the next read / list open.
    todo.forEach(function (n) { if (settled.has("o" + n) && (o.allLeadsLoaded !== false || settled.has("p" + n))) asked.add(n); else failed.add(n); });
    // Only what is NEW goes into the store: a fresh `found` re-runs the
    // linking (and the lookup effect) — never for a lookup that found nothing.
    const have = new Set(S.found.leads.map(function (l) { return l.id; }));
    const newLeads = [];
    ((r && r.leads) || []).forEach(function (l) { if (l && l.id && !have.has(l.id)) { have.add(l.id); newLeads.push(l); } });
    const haveOb = new Set(S.found.outbound.map(function (x) { return x && x.id; }).filter(Boolean));
    const newOb = [];
    ((r && r.outbound) || []).forEach(function (x) { if (x && !(x.id && haveOb.has(x.id))) { if (x.id) haveOb.add(x.id); newOb.push(x); } });
    if (newLeads.length || newOb.length) patch({ found: { leads: S.found.leads.concat(newLeads), outbound: S.found.outbound.concat(newOb) } });
    return true;
  }).catch(function () { todo.forEach(function (n) { busy.delete(n); failed.add(n); }); return false; });
}

// Sign-out: nothing from this session (calls, finds, timers) carries over.
export function resetCallbacksSession() {
  gen++;
  clearTimeout(hangupTimer); hangupTimer = null; hangupAt = 0;
  clearTimeout(wakeTimer); wakeTimer = null;
  inflight = null; lastSettings = null; lastAuthed = false;
  asked.clear(); busy.clear(); failed.clear();
  patch(fresh());
}

// ═══════ one hook for both lists ═══════
function useCallbackList(props) {
  const st = React.useSyncExternalStore(subscribe, snapshot, snapshot);
  const settings = props.settings || null;
  const usable = !!props.on && callLogUsable(settings, props.rcAuthed);
  // Read when the list opens (or RingCentral is switched on / connected) —
  // plus one read at hang-up + 45 s when the last read hasn't caught up with
  // the last hang-up (armHangupRead). A lookup that failed may be tried again.
  useEffect(function () {
    if (!usable) return;
    failed.clear();
    refreshCallbacks(settings, props.rcAuthed);
    armHangupRead();
  }, [usable, settings && settings.rcClientId, settings && settings.rcServerUrl]);// eslint-disable-line react-hooks/exhaustive-deps
  // The clock: re-draws the waits once a minute from what was read.
  // Display only — it NEVER reads RingCentral.
  const [now, setNow] = useState(function () { return Date.now(); });
  useEffect(function () {
    if (!usable) return undefined;
    let t = null;
    const tick = function () { setNow(Date.now()); t = setTimeout(tick, 60000); };
    t = setTimeout(tick, 60000);
    return function () { clearTimeout(t); };
  }, [usable]);
  // A read that came back moves the clock too.
  useEffect(function () { if (st.readAt) setNow(Date.now()); }, [st.readAt]);
  const own = useMemo(function () { return ownNumbers(settings); }, [settings && settings.adminPhone, settings && settings.rcSmsFrom]);// eslint-disable-line react-hooks/exhaustive-deps
  // deviceOnly flips when a mark lands in this device's storage — read it again then.
  const done = useMemo(function () { return doneMarksFrom(st.doneRows, readLocalDone(), st.doneSession); }, [st.doneRows, st.doneSession, st.deviceOnly]);// eslint-disable-line react-hooks/exhaustive-deps
  const list = useMemo(function () { return usable && st.records ? buildCallbacks(st.records, { own: own, done: done, now: now }) : null; }, [usable, st.records, own, done, now]);
  const all = list ? list.rows.concat(list.hidden) : [];
  const numKey = all.map(function (r) { return r.number; }).join(",");
  const links = useMemo(function () {
    return linkCallers(numKey ? numKey.split(",") : [], { loans: props.loans || [], leads: (props.mktgLeads || []).concat(st.found.leads), outbound: st.found.outbound });
  }, [numKey, props.loans, props.mktgLeads, st.found]);// eslint-disable-line react-hooks/exhaustive-deps
  // Numbers no record in memory has: asked of the database once a session
  // (one that failed: once more per read / list open — lookupCallers' `failed`).
  useEffect(function () {
    if (!usable || !numKey) return;
    const need = numKey.split(",").filter(function (n) { return !links.get(n); });
    if (!need.length) return;
    const ids = new Set((props.mktgLeads || []).map(function (m) { return m && m.id; }));
    lookupCallers(need, { allLeadsLoaded: props.allLeadsLoaded, haveLead: function (id) { return ids.has(id); } });
  }, [usable, numKey, links, st.readAt]);// eslint-disable-line react-hooks/exhaustive-deps
  return { st: st, usable: usable, list: list, links: links, now: now };
}

// ═══════ pieces ═══════
const FONT = "'Mulish',system-ui,sans-serif";
const quiet = { fontSize: 12, color: C.textMuted, lineHeight: 1.5 };
const smallBtn = function (on) {
  return { padding: "4px 10px", borderRadius: 7, border: "1px solid " + C.border, background: C.white, color: on ? C.accent : C.textMuted,
    fontSize: 11.5, fontWeight: 700, cursor: on ? "pointer" : "default", fontFamily: FONT, opacity: on ? 1 : 0.55, minHeight: 28 };
};
function ago(ms) { return ms < 60000 ? "just now" : formatWait(ms) + " ago"; }
// Both lists stay compact: about six rows, then they scroll in their own box
// (the Command Center panel sits above everything else on the page).
const PANEL_MAX_H = 300, CARD_MAX_H = 330;

// Keyboard: Done opens the reason chooser with the focus on its first button;
// Cancel / Esc close it and put the focus back on Done (Esc never reaches New
// Call's own Esc — `data-own-esc`); after a reason the row leaves and
// CallbackRows moves the focus to the next row (or the heading).
function CallbackRow({ row, link, practice, onOpen, onDone }) {
  const [asking, setAsking] = useState(false);
  const doneBtn = useRef(null), firstReason = useRef(null), back = useRef(false);
  useEffect(function () {
    if (asking) { if (firstReason.current) firstReason.current.focus(); }
    else if (back.current) { back.current = false; if (doneBtn.current) doneBtn.current.focus(); }
  }, [asking]);
  const closeChooser = function () { back.current = true; setAsking(false); };
  const lab = callerLabel(row, link);
  const lvl = waitLevel(row.waitMs);
  const open = function () { onOpen(row, link); };
  const dnc = link && link.lead && link.lead.doNotContact ? " · opted out of mailings (a call back is fine)" : "";
  return (<div data-callback-row={row.number} data-callback-hidden={row.hidden || undefined} role="button" tabIndex={0}
    onClick={open} onKeyDown={function (e) { if (e.target !== e.currentTarget) return; if (e.key === "Enter" || e.key === " ") { e.preventDefault(); open(); } }}
    title={"Open " + lab.title + " in New Call"}
    style={{ display: "flex", alignItems: "center", gap: "6px 12px", padding: "9px 12px", borderRadius: 8, background: C.white, border: "1px solid " + C.border, cursor: "pointer", flexWrap: "wrap" }}>
    <div style={{ flex: "1 1 200px", minWidth: 0 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: C.text, overflowWrap: "anywhere" }}>
        <span data-callback-name>{lab.title}</span>
        {lab.sub && <span style={{ fontSize: 10.5, color: C.textMuted, fontWeight: 500, marginLeft: 8 }}>{lab.sub}</span>}
      </div>
      <div data-callback-detail style={{ fontSize: 11.5, color: C.textMuted, marginTop: 1, overflowWrap: "anywhere" }}>
        {row.display} · {row.tries === 1 ? "1 missed call" : row.tries + " missed calls"}
        {row.voicemail ? " · left a voicemail" + (row.voicemails > 1 ? " ×" + row.voicemails : "") : ""}
        {row.hidden ? " · " + HIDDEN_LABEL[row.hidden] : ""}{dnc}
      </div>
    </div>
    <span data-callback-wait style={{ fontSize: 12, fontWeight: 800, color: lvl === "day" ? C.feelBad : C.text, whiteSpace: "nowrap" }}>waiting {formatWait(row.waitMs)}</span>
    <span onClick={function (e) { e.stopPropagation(); }} onKeyDown={function (e) { e.stopPropagation(); }} style={{ display: "inline-flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
      <CallBtn phone={row.display} small/>
      {!asking
        ? <button ref={doneBtn} type="button" data-callback-done={row.number} disabled={!!practice} onClick={function () { setAsking(true); }}
            title={practice ? "Practice mode — nothing is saved" : "Mark this call returned (texted back, spam…) — it leaves the list"} style={smallBtn(!practice)}>Done</button>
        : <span data-callback-reasons={row.number} data-own-esc role="group" aria-label="Why it's done"
            onKeyDown={function (e) { if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); closeChooser(); } }}
            style={{ display: "inline-flex", gap: 4, flexWrap: "wrap", alignItems: "center" }}>
            {[""].concat(DONE_REASONS).map(function (r, i) {
              return <button key={r || "none"} ref={i === 0 ? firstReason : undefined} type="button" data-callback-reason={r || "none"} onClick={function () { setAsking(false); onDone(row, r, link); }} style={smallBtn(true)}>{r || "Done"}</button>;
            })}
            <button type="button" data-callback-cancel onClick={closeChooser} style={Object.assign(smallBtn(true), { color: C.textMuted, border: "none", background: "transparent" })}>Cancel</button>
          </span>}
    </span>
  </div>);
}

// The rows + the hidden toggle + the quiet lines under them. `maxHeight`
// caps each list (it scrolls inside its own box), so a pile of unreturned
// callers never pushes the rest of the page down. After a Done the keyboard
// goes to the next row, else the first row left, else `homeRef` (the heading).
function CallbackRows({ v, practice, onOpen, onDone, maxHeight, homeRef }) {
  const [showHidden, setShowHidden] = useState(false);
  const box = useRef(null), after = useRef(null);
  const rows = v.list.rows, hidden = v.list.hidden;
  const shared = !v.st.deviceOnly && v.st.doneShared !== false;
  const doneThenMove = function (row, reason, link) {
    const list = row.hidden ? hidden : rows;
    const i = list.findIndex(function (r) { return r.number === row.number; });
    const next = list[i + 1] || list[i - 1] || null;
    after.current = { number: row.number, next: next ? next.number : null, waits: 0 };
    onDone(row, reason, link);
  };
  useEffect(function () {
    const a = after.current;
    if (!a || !box.current) return;
    // He has already moved on (clicked / tabbed somewhere else) — leave the focus be.
    const ae = document.activeElement;
    if (ae && ae !== document.body && !box.current.contains(ae)) { after.current = null; return; }
    const q = function (n) { return n ? box.current.querySelector('[data-callback-row="' + n + '"]') : null; };
    const still = q(a.number);
    if (still && a.waits < 2) { a.waits++; still.focus(); return; }   // the mark hasn't reached the list yet
    after.current = null;
    const el = still || q(a.next) || box.current.querySelector("[data-callback-row]");
    if (el) el.focus();
    else if (homeRef && homeRef.current) homeRef.current.focus();
  });
  const scroll = { maxHeight: maxHeight || "none", overflowY: maxHeight ? "auto" : "visible" };
  return (<div ref={box}>
    {rows.length === 0
      ? <div data-testid="callbacks-empty" style={quiet}>{ALL_RETURNED}</div>
      : <div data-testid="callbacks-rows" style={Object.assign({ display: "flex", flexDirection: "column", gap: 6 }, scroll)}>
          {rows.map(function (r) { return <CallbackRow key={r.number} row={r} link={v.links.get(r.number)} practice={practice} onOpen={onOpen} onDone={doneThenMove}/>; })}
        </div>}
    {hidden.length > 0 && <div style={{ marginTop: 8 }}>
      <button type="button" data-testid="callbacks-show-hidden" aria-expanded={showHidden} onClick={function () { setShowHidden(!showHidden); }}
        style={{ border: "none", background: "transparent", padding: 0, color: C.accentLight, fontSize: 11.5, fontWeight: 700, cursor: "pointer", fontFamily: FONT }}>
        {showHidden ? "Hide the " + hidden.length + " hidden" : "show " + hidden.length + " hidden"}
      </button>
      <span style={{ fontSize: 11, color: C.textMuted }}> · your own numbers, toll-free and vendor / lender caller IDs</span>
      {showHidden && <div data-testid="callbacks-hidden" style={Object.assign({ display: "flex", flexDirection: "column", gap: 6, marginTop: 6 }, scroll)}>
        {hidden.map(function (r) { return <CallbackRow key={r.number} row={r} link={v.links.get(r.number)} practice={practice} onOpen={onOpen} onDone={doneThenMove}/>; })}
      </div>}
    </div>}
    {!shared && <div data-testid="callbacks-device-only" style={Object.assign({}, quiet, { fontSize: 11, marginTop: 8 })}>Done marks saved on this device only</div>}
    {shared && v.st.doneReadFailed && v.st.status !== "error" && <div data-testid="callbacks-done-unread" style={Object.assign({}, quiet, { fontSize: 11, marginTop: 8 })}>Couldn't read the Done marks just now — Refresh to try again.</div>}
    {v.st.status === "error" && v.st.error === "rate_limited" && <div style={Object.assign({}, quiet, { fontSize: 11, marginTop: 8 })}>RingCentral asked to wait a moment — this is the list from {ago(Date.now() - v.st.readAt)}.</div>}
    {v.st.status === "error" && v.st.error !== "rate_limited" && <div style={Object.assign({}, quiet, { fontSize: 11, marginTop: 8 })}>Couldn't read the call log just now — this is the list from {ago(Date.now() - v.st.readAt)}.</div>}
  </div>);
}

function openLine(settings, rcAuthed) {
  if (!settings || !settings.rcEnabled) return "RingCentral is off — turn it on in Settings to see calls to return.";
  if (!rcAuthed) return "Connect RingCentral in Settings to see calls to return.";
  return "";
}
function errorLine(code) {
  if (code === "no_permission") return PERMISSION_LINE;
  if (code === "not_authenticated") return "RingCentral needs you to connect again — Settings → Connect RingCentral";
  if (code === "rate_limited") return "RingCentral asked to wait a moment — Refresh in a minute";
  return "Couldn't read the RingCentral call log — Refresh to try again";
}

// Refresh — on both lists. Gated like every read (once a minute, Retry-After);
// its title says how long to wait.
function RefreshButton({ v, settings, rcAuthed }) {
  const st = v.st;
  if (!v.usable) return null;
  const gate = readGate(st, Date.now());
  const can = st.status !== "loading" && gate.ok;
  return (<button type="button" data-testid="callbacks-refresh" disabled={!can} onClick={function () { refreshCallbacks(settings, rcAuthed); }}
    title={st.status === "loading" ? "Reading…" : gate.ok ? "Read the RingCentral call log again" : "RingCentral is read at most once a minute — try again in " + Math.ceil(gate.waitMs / 1000) + " s"}
    style={smallBtn(can)}>{st.status === "loading" ? "Reading…" : "Refresh"}</button>);
}

// "How the call log was read" — result values seen, counts only, inbound and
// outbound apart (his own calls say "No Answer" / "Busy" too), and what the
// inbound ones were counted as.
function countsLine(o) { return Object.keys(o || {}).sort().map(function (k) { return k + " " + o[k]; }).join(" · ") || "none"; }
function ResultsSeen({ list }) {
  const c = list.counts, res = list.results || {};
  const other = res.other && Object.keys(res.other).length ? res.other : null;
  return (<details data-testid="callbacks-results" style={{ marginTop: 10 }}>
    <summary style={{ fontSize: 11, color: C.textMuted, cursor: "pointer" }}>How the call log was read</summary>
    <div style={{ fontSize: 11, color: C.textMuted, marginTop: 4, lineHeight: 1.5 }}>
      <div>Last {CALLBACK_LOOKBACK_DAYS} days, {c.records} call{c.records === 1 ? "" : "s"}.</div>
      <div data-callback-results="inbound">Inbound results: {countsLine(res.inbound)} — counted as {c.missed} missed, {c.answered} answered{c.blocked ? ", " + c.blocked + " blocked" : ""}{c.inProgress ? ", " + c.inProgress + " in progress" : ""}{c.internal ? ", " + c.internal + " with no caller number" : ""}.</div>
      <div data-callback-results="outbound">Your outbound results: {countsLine(res.outbound)}.</div>
      {other && <div data-callback-results="other">No direction: {countsLine(other)}.</div>}
      <div>Missed = inbound and not Accepted / Call connected / In Progress / Blocked; returned = an outbound call to them, a later answered call from them, or Done.</div>
    </div>
  </details>);
}

// Rang on THIS computer (lib.js's ring buffer) — the fallback when the call
// log can't be read. It can't tell a returned call from one that wasn't.
function RingBufferRows({ loans, mktgLeads, onStartCall }) {
  const now = Date.now();
  const list = readCallLog().filter(function (c) { return c && !c.handled && c.epoch && now - c.epoch < CALLBACK_LOOKBACK_DAYS * 864e5; }).slice(0, 8);
  const open = function (c) {
    if (!onStartCall) return;
    if (c.matchKind === "loan") { const l = (loans || []).find(function (x) { return x.id === c.matchId; }); if (l) { onStartCall({ prospect: prospectFromLoan(l), callEpoch: c.epoch }); return; } }
    if (c.matchKind === "lead") { const m = (mktgLeads || []).find(function (x) { return x.id === c.matchId; }); if (m) { onStartCall({ prospect: prospectFromLead(m), callEpoch: c.epoch }); return; } }
    onStartCall({ phone: c.from || "", callEpoch: c.epoch });
  };
  return (<div data-testid="callbacks-ring-buffer" style={{ marginTop: 10 }}>
    <div style={{ fontSize: 11, fontWeight: 800, color: C.textMuted, textTransform: "uppercase", letterSpacing: .6, marginBottom: 6 }}>Rang your line on this computer</div>
    {list.length === 0 ? <div style={quiet}>Nothing has rung here lately.</div>
      : <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>{list.map(function (c) {
          const n = numberKey(c.from);
          return (<div key={c.epoch} data-ring-row={n} role="button" tabIndex={0} onClick={function () { open(c); }}
            onKeyDown={function (e) { if (e.target !== e.currentTarget) return; if (e.key === "Enter" || e.key === " ") { e.preventDefault(); open(c); } }}
            style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", borderRadius: 8, background: C.white, border: "1px solid " + C.border, cursor: "pointer", flexWrap: "wrap" }}>
            <span style={{ flex: "1 1 180px", minWidth: 0, fontSize: 12.5, fontWeight: 700, color: C.text, overflowWrap: "anywhere" }}>{c.matchName || c.fromName || formatNumber(n) || "Unknown number"}</span>
            <span style={{ fontSize: 11.5, color: C.textMuted, whiteSpace: "nowrap" }}>{formatNumber(n)} · rang {ago(now - c.epoch)}</span>
          </div>);
        })}</div>}
  </div>);
}

// ═══════ Command Center: the panel at the top ═══════
export function CallbacksPanel({ staff, settings, rcAuthed, loans, mktgLeads, adoptLeads, allLeadsLoaded, onStartCall, practice }) {
  const acc = staffAccess(staff);
  const on = !!settings && !!acc.dashboard && !!acc.newCall;
  const v = useCallbackList({ on: on, settings: settings, rcAuthed: rcAuthed, loans: loans, mktgLeads: mktgLeads, allLeadsLoaded: allLeadsLoaded });
  const home = useRef(null);
  if (!on) return null;
  const st = v.st;
  const openRow = function (row, link) {
    if (!onStartCall) return;
    if (link && link.kind === "loan" && link.loan) { onStartCall({ prospect: prospectFromLoan(link.loan) }); return; }
    if (link && link.kind === "lead" && link.lead) {
      if (adoptLeads && !(mktgLeads || []).some(function (m) { return m && m.id === link.lead.id; })) adoptLeads([link.lead]);
      onStartCall({ prospect: prospectFromLead(link.lead) });
      return;
    }
    onStartCall({ phone: row.display });
  };
  const done = function (row, reason, link) { markCallbackDone(row, reason, { practice: practice, link: link }); };
  const off = openLine(settings, rcAuthed);
  return (<section id="cc-callbacks" data-testid="callbacks-panel" aria-label="Calls to return"
    style={{ background: C.card, borderRadius: 16, border: "1px solid " + C.border, padding: "14px 18px", marginBottom: 20, scrollMarginTop: 90 }}>
    <div style={{ display: "flex", alignItems: "center", gap: "4px 10px", flexWrap: "wrap", marginBottom: off ? 0 : 10 }}>
      <h3 ref={home} tabIndex={-1} data-callbacks-heading style={{ margin: 0, fontSize: 15, fontWeight: 800, color: C.text, letterSpacing: .2, outline: "none" }}>Calls to return</h3>
      {v.list && v.list.rows.length > 0 && <span data-testid="callbacks-count" style={{ fontSize: 12, color: C.textMuted }}>{v.list.rows.length} waiting · longest first</span>}
      <span style={{ flex: 1 }}/>
      {v.usable && st.readAt > 0 && <span style={{ fontSize: 11, color: C.textMuted }}>read {ago(Date.now() - st.readAt)}</span>}
      <RefreshButton v={v} settings={settings} rcAuthed={rcAuthed}/>
    </div>
    {off ? <div data-testid="callbacks-off" style={Object.assign({}, quiet, { marginTop: 4 })}>{off}</div>
      : v.list ? <CallbackRows v={v} practice={practice} onOpen={openRow} onDone={done} maxHeight={PANEL_MAX_H} homeRef={home}/>
      : st.status === "loading" || st.status === "idle" ? <div style={quiet}>Reading your RingCentral call log…</div>
      : <div>
          <div data-testid={st.error === "no_permission" ? "callbacks-permission" : "callbacks-error"} style={Object.assign({}, quiet, { color: C.text, fontWeight: 600 })}>{errorLine(st.error)}</div>
          <RingBufferRows loans={loans} mktgLeads={mktgLeads} onStartCall={onStartCall}/>
        </div>}
    {v.list && <ResultsSeen list={v.list}/>}
  </section>);
}

// ═══════ New Call: the "Rang your line" spot ═══════
// The account-wide list when the call log can be read; otherwise `fallback`
// (the console's per-device "Rang your line" card) — under the one line that
// says what to do when it's the permission.
export function CallbacksCard({ staff, settings, rcAuthed, loans, mktgLeads, allLeadsLoaded, practice, onOpen, fallback }) {
  const acc = staffAccess(staff);
  const on = !!settings && !!acc.newCall;
  const v = useCallbackList({ on: on, settings: settings, rcAuthed: rcAuthed, loans: loans, mktgLeads: mktgLeads, allLeadsLoaded: allLeadsLoaded });
  const home = useRef(null);
  if (!on || !v.usable) return fallback || null;
  const st = v.st;
  const done = function (row, reason, link) { markCallbackDone(row, reason, { practice: practice, link: link }); };
  if (!v.list && st.status !== "loading" && st.status !== "idle") {
    return (<div data-testid="callbacks-card-fallback" style={{ display: "flex", flexDirection: "column", gap: 8, minWidth: 0 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", padding: "0 4px" }}>
        <div data-testid={st.error === "no_permission" ? "callbacks-permission" : "callbacks-error"} style={Object.assign({}, quiet, { color: C.text, fontWeight: 600, flex: "1 1 200px", minWidth: 0 })}>{errorLine(st.error)}</div>
        <RefreshButton v={v} settings={settings} rcAuthed={rcAuthed}/>
      </div>
      {fallback}
    </div>);
  }
  const color = C.clay;
  return (<div data-testid="callbacks-card" style={{ background: C.card, border: "1px solid " + color + "38", borderRadius: 14, padding: "16px 18px", minWidth: 0 }}>
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
      <h3 ref={home} tabIndex={-1} data-callbacks-heading style={{ margin: 0, fontSize: 13, fontWeight: 800, color: C.clayDeep, outline: "none" }}>Calls to return</h3>
      {v.list && v.list.rows.length > 0 && <span style={{ fontSize: 11, color: C.textMuted }}>{v.list.rows.length} waiting</span>}
      <span style={{ flex: 1 }}/>
      <RefreshButton v={v} settings={settings} rcAuthed={rcAuthed}/>
    </div>
    {v.list ? <CallbackRows v={v} practice={practice} onOpen={onOpen} onDone={done} maxHeight={CARD_MAX_H} homeRef={home}/>
      : <div style={quiet}>Reading your RingCentral call log…</div>}
  </div>);
}

// Tests only.
export const _testing = {
  reset: resetCallbacksSession,
  state: function () { return S; },
  listeners: function () { return listeners.size; },
  asked: function () { return Array.from(asked); },
};
